import React, { FC, ReactNode } from "react";
import { Avatar, Tooltip } from "antd";
import { TooltipPlacement } from "antd/es/tooltip";
import useAuthenticatedImage from "../hooks/useAuthenticatedImage";
import "../customAvatar/CustomAvatar.scss";

type AuthenticatedAvatarProps = {
  size: number | "large" | "small" | "default";
  avatarUrl: string | null | undefined;
  fallback: string; // Initials or default avatar URL
  icon?: ReactNode;
  fontSize?: number;
  tooltipTitle?: string;
  placement?: TooltipPlacement;
  className?: string;
};

/**
 * Avatar component that fetches images from authenticated endpoints.
 * Uses JWT token for authorization and falls back to initials on error.
 */
const AuthenticatedAvatar: FC<AuthenticatedAvatarProps> = ({
  size,
  avatarUrl,
  fallback,
  icon,
  fontSize,
  tooltipTitle,
  placement,
  className,
}) => {
  const { blobUrl, error } = useAuthenticatedImage(avatarUrl);

  // Determine what to display as src
  // - If we have a blob URL, use it
  // - If loading or error, use fallback (initials will render as text inside Avatar)
  const displaySrc = blobUrl && !error ? blobUrl : undefined;
  const displayFallback = !displaySrc ? fallback : undefined;

  return (
    <Tooltip placement={placement} title={tooltipTitle}>
      <span>
        <Avatar
          size={size}
          icon={icon}
          src={displaySrc}
          style={fontSize ? { fontSize: fontSize } : undefined}
          className={className || "custom-avatar"}
        >
          {displayFallback}
        </Avatar>
      </span>
    </Tooltip>
  );
};

export default AuthenticatedAvatar;
